import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { articleMatchesSearch } from 'pages/shipping/editOrNewShipping/utils/articleSearchNormalization';
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { setFilters, resetFilters } from 'reducers/filtersArticlesSlice';
import { resetPagination } from 'reducers/paginationArticlesSlice';
import { useNavigate } from 'react-router-dom';
import { ENDPOINT, LIMIT_PAGE, NON_ERASABLE_ARTICLE_ID } from "constants/variables";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import TextField from '@mui/material/TextField';
import "css/generalSettings.css";
import "css/customDialog.css";
import { successErrorAction, warningErrorAction } from "actions/ErrorActions";
import { helpHttp } from "service/HelpHttp";
import { setLoading } from 'reducers/loadingSlice';
import { LoadingNews } from "components/utils/LoadingNews";
import TableSortingArticle from "pages/articles/articlesFilterList/SortingTablesArticles";
import ViewArticleDetails from "pages/articles/articlesFilterList/ViewArticleDetails";
import exportArticleExcelOneSheet from "pages/articles/articlesFilterList/ArticleExportToExcelDetails";
import PropTypes from 'prop-types';

const initialFiltersArticle = {
  idFrom: "",
  idTo: "",
  titleesFrom: "",
  titleesTo: "",
  titleenFrom: "",
  titleenTo: "",
  owncodeFrom: "",
  owncodeTo: "",
  codeanFrom: "",
  codeanTo: "",
  barcodeFrom: "",
  barcodeTo: "",
  accountingcodeFrom: "",
  accountingcodeTo: "",
  marcaFrom: "",
  marcaTo: "",
  modeloFrom: "",
  modeloTo: "",
  locationFrom: "",
  locationTo: "",
  applytypesofsize: true,
  notapplytypesofsize: true,
  applycolor: true,
  notapplycolor: true,
  applynumserial: true,
  notapplynumserial: true,
  isnewpiece: true,
  notisnewpiece: true,
  minimumstockFrom: "",
  minimumstockTo: "",
  maximumstockFrom: "",
  maximumstockTo: "",
  actualstockFrom: "",
  actualstockTo: "",
  salesunitsFromES: "",
  salesunitsToES: "",
  salesunitsFromEN: "",
  salesunitsToEN: "",
  departmentFromES: "",
  departmentToES: "",
  departmentFromEN: "",
  departmentToEN: "",
  sectionFromES: "",
  sectionToES: "",
  sectionFromEN: "",
  sectionToEN: "",
  subsectionFromES: "",
  subsectionToES: "",
  subsectionFromEN: "",
  subsectionToEN: ""
};

const emptyArticle = {
  id: 0,
  titlees: "",
  titleen: "",
  owncode: "",
  codean: "",
  barcode: "",
  accountingcode: "",
  marca: "",
  model: "",
  location: "",
  applytypesofsize: true,
  applycolor: true,
  applynumserial: true,
  isnewpiece: true,
  minimumstock: 0,
  maximumstock: 0,
  actualstock: 0,
  salesunits_id: 0,
  salesunits_namees: "",
  salesunits_nameen: "",
  department_id: 0,
  department_namees: "",
  department_nameen: "",
  section_id: 0,
  section_namees: "",
  section_nameen: "",
  subsection_id: 0,
  subsection_namees: "",
  subsection_nameen: "",
  color_id: 0,
  codehex: "",
  codeler_id: 0,
  codeler_code: "",
  codeler_namees: "",
  codeler_nameen: ""
};

const initialStateArticle = [emptyArticle];

const initialTypesOfUnitToSelect = [{
  id: 0,
  namees: "",
  nameen: "",
  symbol: ""
}];

const initialFilterToSelect = [{
  id: 0,
  code: "",
  namees: "",
  nameen: ""
}];

const TEXT_FIELD_STYLE = { color: "#4e73df", fontSize: 18, textAlign: 'right' };
const INPUT_LABEL_STYLE = { fontSize: '18px', background: '#FFFFFF' };
const INPUT_STYLE_382 = { width: 382, fontSize: '18px' };
const CHECKBOX_SX = { '& .MuiSvgIcon-root': { fontSize: 28 } };

const hasValue = (value) => value !== "";
const isInvalidNumber = (value) => hasValue(value) && !/^[0-9]+$/.test(value);
const hasInvalidCharacters = (value) => /[!$%&|<>?{}~"']+/.test(value || "");

const INVALID_CHAR_FIELDS = [
  "titleesFrom",
  "titleesTo",
  "titleenFrom",
  "titleenTo",
  "owncodeFrom",
  "owncodeTo",
  "barcodeFrom",
  "barcodeTo",
  "accountingcodeFrom",
  "accountingcodeTo",
  "locationFrom",
  "locationTo"
];

const NUMERIC_RANGE_FIELDS = [
  {
    from: "idFrom",
    to: "idTo",
    invalidError: "Text_TextNoNumber",
    rangeError: "Text_TextGreater_Id"
  },
  {
    from: "minimumstockFrom",
    to: "minimumstockTo",
    invalidError: "Text_TextNoNumber",
    rangeError: "Text_TextGreater_Minimumstock"
  },
  {
    from: "maximumstockFrom",
    to: "maximumstockTo",
    invalidError: "Text_TextNoNumber",
    rangeError: "Text_TextGreater_Maximumstock"
  },
  {
    from: "actualstockFrom",
    to: "actualstockTo",
    invalidError: "Text_TextNoNumber",
    rangeError: "Text_TextGreater_Actualstock"
  }
];

const LEXICAL_RANGE_FIELDS = [
  { from: "titleesFrom", to: "titleesTo", error: "Text_TextGreater_title" },
  { from: "titleenFrom", to: "titleenTo", error: "Text_TextGreater_title" },
  { from: "owncodeFrom", to: "owncodeTo", error: "Text_TextGreater_owncode" },
  { from: "codeanFrom", to: "codeanTo", error: "Text_TextGreater_codean" },
  { from: "barcodeFrom", to: "barcodeTo", error: "Text_TextGreater_barcode" },
  { from: "accountingcodeFrom", to: "accountingcodeTo", error: "Text_TextGreater_accountingcode" },
  { from: "locationFrom", to: "locationTo", error: "Text_TextGreater_Location" },
  { from: "salesunitsFromES", to: "salesunitsToES", error: "Text_TextGreater_salesunits" },
  { from: "salesunitsFromEN", to: "salesunitsToEN", error: "Text_TextGreater_salesunits" },
  { from: "departmentFromES", to: "departmentToES", error: "Text_TextGreater_department" },
  { from: "departmentFromEN", to: "departmentToEN", error: "Text_TextGreater_department" },
  { from: "sectionFromES", to: "sectionToES", error: "Text_TextGreater_section" },
  { from: "sectionFromEN", to: "sectionToEN", error: "Text_TextGreater_section" },
  { from: "subsectionFromES", to: "subsectionToES", error: "Text_TextGreater_subsection" },
  { from: "subsectionFromEN", to: "subsectionToEN", error: "Text_TextGreater_subsection" }
];

const OTHER_FILTER_KEYS = [
  "salesunitsFromES",
  "salesunitsToES",
  "salesunitsFromEN",
  "salesunitsToEN",
  "departmentFromES",
  "departmentToES",
  "departmentFromEN",
  "departmentToEN",
  "sectionFromES",
  "sectionToES",
  "sectionFromEN",
  "sectionToEN",
  "subsectionFromES",
  "subsectionToES",
  "subsectionFromEN",
  "subsectionToEN",
  "marcaFrom",
  "marcaTo",
  "locationFrom",
  "locationTo",
  "minimumstockFrom",
  "minimumstockTo",
  "maximumstockFrom",
  "maximumstockTo",
  "actualstockFrom",
  "actualstockTo"
];

const buildOffsetRange = (total) => {
  if (total <= LIMIT_PAGE) {
    return [{ value: 0, label: ` 1 - ${Number(total).toLocaleString('de-DE')}` }];
  }

  const ranges = [];
  for (let i = 0; i < total; i += Number(LIMIT_PAGE)) {
    const start = i + 1;
    const end = Math.min(i + Number(LIMIT_PAGE), total);
    ranges.push({
      value: i,
      label: ` ${start.toLocaleString('de-DE')} - ${end.toLocaleString('de-DE')}`
    });
  }

  return ranges;
};

const hasMenuAccess = (roles, menuName) =>
  !!roles?.[0]?.listmenus?.some((item) => item.name === menuName);

const FilterChip = ({ active, label, onClick }) => (
  <button
    type="button"
    className={`formdi-pending-chip ${active ? "formdi-pending-chip--active" : ""}`}
    onClick={onClick}
  >
    <span className={`formdi-pending-dot ${active ? "formdi-pending-dot--on" : ""}`} />
    {label}
  </button>
);

FilterChip.propTypes = {
  active: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired
};

const RangeTextField = ({
  fromId,
  toId,
  fromName,
  toName,
  fromValue,
  toValue,
  fromLabel,
  toLabel,
  fromPlaceholder,
  toPlaceholder,
  onBlur,
  onChange,
  width = 400
}) => (
  <div className="form-group">
    <TextField
      id={fromId}
      name={fromName}
      value={fromValue}
      style={TEXT_FIELD_STYLE}
      InputLabelProps={{ style: INPUT_LABEL_STYLE }}
      inputProps={{ style: { width, fontSize: '18px' }, maxLength: 100 }}
      label={fromLabel}
      placeholder={fromPlaceholder}
      variant="outlined"
      onBlur={onBlur}
      onChange={onChange}
    />
    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
    <TextField
      id={toId}
      name={toName}
      value={toValue}
      style={TEXT_FIELD_STYLE}
      InputLabelProps={{ style: INPUT_LABEL_STYLE }}
      inputProps={{ style: { width, fontSize: '18px' }, maxLength: 100 }}
      label={toLabel}
      placeholder={toPlaceholder}
      variant="outlined"
      onBlur={onBlur}
      onChange={onChange}
    />
  </div>
);

RangeTextField.propTypes = {
  fromId: PropTypes.string.isRequired,
  toId: PropTypes.string.isRequired,
  fromName: PropTypes.string.isRequired,
  toName: PropTypes.string.isRequired,
  fromValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  toValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fromLabel: PropTypes.string.isRequired,
  toLabel: PropTypes.string.isRequired,
  fromPlaceholder: PropTypes.string.isRequired,
  toPlaceholder: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  width: PropTypes.number
};

const RangeSelectField = ({
  fromId,
  toId,
  fromName,
  toName,
  fromValue,
  toValue,
  fromLabel,
  toLabel,
  fromPlaceholder,
  toPlaceholder,
  options,
  optionTextKey,
  onBlur,
  onChange
}) => (
  <div className="form-group">
    <TextField
      id={fromId}
      name={fromName}
      value={fromValue}
      style={TEXT_FIELD_STYLE}
      InputLabelProps={{ style: INPUT_LABEL_STYLE }}
      inputProps={{ style: INPUT_STYLE_382, maxLength: 100 }}
      label={fromLabel}
      placeholder={fromPlaceholder}
      variant="outlined"
      onBlur={onBlur}
      onChange={onChange}
      select
      SelectProps={{ native: true }}
    >
      <option key={0} value="" />
      {options.map((option) => (
        <option key={option.id} value={option[optionTextKey]}>
          {option[optionTextKey]}
        </option>
      ))}
    </TextField>

    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;

    <TextField
      id={toId}
      name={toName}
      value={toValue}
      style={TEXT_FIELD_STYLE}
      InputLabelProps={{ style: INPUT_LABEL_STYLE }}
      inputProps={{ style: INPUT_STYLE_382, maxLength: 100 }}
      label={toLabel}
      placeholder={toPlaceholder}
      variant="outlined"
      onBlur={onBlur}
      onChange={onChange}
      select
      SelectProps={{ native: true }}
    >
      <option key={0} value="" />
      {options
        .filter((item) => item[optionTextKey] >= fromValue)
        .map((option) => (
          <option key={option.id} value={option[optionTextKey]}>
            {option[optionTextKey]}
          </option>
        ))}
    </TextField>
  </div>
);

RangeSelectField.propTypes = {
  fromId: PropTypes.string.isRequired,
  toId: PropTypes.string.isRequired,
  fromName: PropTypes.string.isRequired,
  toName: PropTypes.string.isRequired,
  fromValue: PropTypes.string.isRequired,
  toValue: PropTypes.string.isRequired,
  fromLabel: PropTypes.string.isRequired,
  toLabel: PropTypes.string.isRequired,
  fromPlaceholder: PropTypes.string.isRequired,
  toPlaceholder: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  optionTextKey: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired
};

const BooleanFilterPair = ({
  leftId,
  leftName,
  leftChecked,
  leftLabel,
  rightId,
  rightName,
  rightChecked,
  rightLabel,
  onToggle
}) => (
  <div className="form-group">
    <Checkbox
      id={leftId}
      name={leftName}
      checked={leftChecked}
      onChange={() => onToggle(leftName)}
      variant="standard"
      sx={CHECKBOX_SX}
    />
    &nbsp;&nbsp;
    <label className="text-primary" style={{ fontSize: 18 }}>{leftLabel}</label>
    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
    <Checkbox
      id={rightId}
      name={rightName}
      checked={rightChecked}
      onChange={() => onToggle(rightName)}
      variant="standard"
      sx={CHECKBOX_SX}
    />
    &nbsp;&nbsp;
    <label className="text-primary" style={{ fontSize: 18 }}>{rightLabel}</label>
  </div>
);

BooleanFilterPair.propTypes = {
  leftId: PropTypes.string.isRequired,
  leftName: PropTypes.string.isRequired,
  leftChecked: PropTypes.bool.isRequired,
  leftLabel: PropTypes.string.isRequired,
  rightId: PropTypes.string.isRequired,
  rightName: PropTypes.string.isRequired,
  rightChecked: PropTypes.bool.isRequired,
  rightLabel: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired
};

const ArticleFilter = ({ showModalArticlesSettings, setShowModalArticlesSettings }) => {
  const { t, i18n } = useTranslation(['listDataArticles']);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const token = useSelector((state) => state.loginUser.token);
  const roles = useSelector((state) => state.loginUser.roles);
  const DELETE_CODE = useSelector((state) => state.formatDIUser.delete_code);
  const DELETE_CODE_STORE = useSelector((state) => state.formatDIUser.delete_code_store);
  const filtersArticlesDetails = useSelector((state) => state.filtersArticles);
  const isLoadingData = useSelector((state) => state.loading.isLoading);

  const [dataArticle, setDataArticle] = useState(initialStateArticle);
  const [filtersArticle, setFiltersArticle] = useState(filtersArticlesDetails);
  const [numTotRegFilter, setNumTotRegFilter] = useState(0);
  const [numTotRegArticle, setNumTotRegArticle] = useState(0);
  const [errorsFiltersArticle, setErrorsFiltersArticle] = useState({});
  const [inputTextArticle, setInputTextArticle] = useState("");
  const [openDeleteRowArticle, setOpenDeleteRowArticle] = useState(false);
  const [openEditRowArticle, setOpenEditRowArticle] = useState(false);
  const [openEditFilterArticle, setOpenEditFilterArticle] = useState(false);
  const [rowEditArticle, setRowEditArticle] = useState(emptyArticle);
  const [isLoadingErrorArticle, setIsLoadingErrorArticle] = useState(false);
  const [idCodeDelete, setIdCodeDelete] = useState(DELETE_CODE);
  const [activeOffsetArticle, setActiveOffsetArticle] = useState(false);
  const [currencyOffsetArticle, setCurrencyOffsetArticle] = useState(0);
  const [rangeOffsetArticle, setRangeOffsetArticle] = useState([{}]);
  const [refeshData, setRefeshData] = useState(false);
  const [openViewDetails, setOpenViewDetails] = useState(false);
  const [typesOfUnitsToSelect, setTypesOfUnitsToSelect] = useState(initialTypesOfUnitToSelect);
  const [departmentToSelect, setDepartmentToSelect] = useState(initialFilterToSelect);
  const [sectionToSelect, setSectionToSelect] = useState(initialFilterToSelect);
  const [subSectionToSelect, setSubSectionToSelect] = useState(initialFilterToSelect);
  const [openVideoHelpStore, setOpenVideoHelpStore] = useState(false);
  const STORE_VIDEO_URL = "https://dn3b0526k2h3y.cloudfront.net/ARTICULOS.mp4";
  
  const isSpanish = i18n.language === "es";
  const selectLabelKey = isSpanish ? "namees" : "nameen";

  const permissions = useMemo(() => ({
    showCreateButton: hasMenuAccess(roles, 'SubMenu_Articles_Create'),
    showEditButton: hasMenuAccess(roles, 'SubMenu_Articles_Edit'),
    showDeleteButton: hasMenuAccess(roles, 'SubMenu_Articles_Delete'),
    showExcelButton: hasMenuAccess(roles, "SubMenu_Articles_Handle_Excel"),
    showExtendedFilter: hasMenuAccess(roles, "Handle_Article_View_Extemded")
  }), [roles]);

  const validateFormFilterArticle = useCallback((sourceFilters = filtersArticle) => {
    const errors = {};

    NUMERIC_RANGE_FIELDS.forEach(({ from, to, invalidError, rangeError }) => {
      if (isInvalidNumber(sourceFilters[from])) {
        errors[from] = invalidError;
      }

      if (isInvalidNumber(sourceFilters[to])) {
        errors[to] = invalidError;
      }

      if (
        hasValue(sourceFilters[from]) &&
        hasValue(sourceFilters[to]) &&
        !errors[from] &&
        !errors[to] &&
        parseInt(sourceFilters[to], 10) < parseInt(sourceFilters[from], 10)
      ) {
        errors[to] = rangeError;
      }
    });

    INVALID_CHAR_FIELDS.forEach((field) => {
      if (hasInvalidCharacters(sourceFilters[field])) {
        errors[field] = 'Text_TextNoValid';
      }
    });

    LEXICAL_RANGE_FIELDS.forEach(({ from, to, error }) => {
      if (hasValue(sourceFilters[to]) && sourceFilters[to] < sourceFilters[from]) {
        errors[to] = error;
      }
    });

    return errors;
  }, [filtersArticle]);

  const resetArticleState = useCallback(() => {
    setDataArticle(initialStateArticle);
    setFiltersArticle(initialFiltersArticle);
    setNumTotRegFilter(0);
    setNumTotRegArticle(0);
    setErrorsFiltersArticle({});
    setInputTextArticle("");
    setOpenDeleteRowArticle(false);
    setOpenEditRowArticle(false);
    setOpenEditFilterArticle(false);
    setRowEditArticle(emptyArticle);
    setIsLoadingErrorArticle(false);
    setIdCodeDelete(DELETE_CODE);
    setActiveOffsetArticle(false);
    setCurrencyOffsetArticle(0);
    setRangeOffsetArticle([{}]);
    setShowModalArticlesSettings(false);
    dispatch(setLoading(false));
    dispatch(resetPagination());
    dispatch(resetFilters());
    setRefeshData(false);
    setOpenViewDetails(false);
    setOpenVideoHelpStore(false);  
    setTypesOfUnitsToSelect(initialTypesOfUnitToSelect);
    setDepartmentToSelect(initialFilterToSelect);
    setSectionToSelect(initialFilterToSelect);
    setSubSectionToSelect(initialFilterToSelect);
    navigate("/home");
  }, [DELETE_CODE, dispatch, navigate, setShowModalArticlesSettings]);

  const closeModalArticles = useCallback(() => {
    resetArticleState();
  }, [resetArticleState]);

  const openCreateOrEditDataArticle = useCallback(() => {
    setInputTextArticle("");
    navigate("/articledetail/0");
  }, [navigate]);

  const openEditFiltersArticle = useCallback(() => {
    setOpenEditFilterArticle((prev) => !prev);
  }, []);

  const handleSubmitFilterEditArticle = useCallback(() => {
    const validationErrors = validateFormFilterArticle(filtersArticle);
    setErrorsFiltersArticle(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      dispatch(resetPagination());
      dispatch(setFilters(filtersArticle));
      setOpenEditFilterArticle(false);
      return;
    }

    dispatch(warningErrorAction(t('Text_Error_Filter')));
  }, [dispatch, filtersArticle, t, validateFormFilterArticle]);

  const handleDeleteFiltersStore = useCallback(() => {
    setErrorsFiltersArticle({});
    dispatch(resetFilters());
    dispatch(resetPagination());
    setFiltersArticle(initialFiltersArticle);
    setOpenEditFilterArticle(false);
  }, [dispatch]);

  const handleCancelFiltersArticle = useCallback(() => {
    setErrorsFiltersArticle({});
    setOpenEditFilterArticle(false);
  }, []);

  const handleChangeFilterEditArticle = useCallback((e) => {
    const { name, value } = e.target;
    setFiltersArticle((prev) => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleBlurFilterEditArticle = useCallback((e) => {
    const { name, value } = e.target;
    const nextFilters = {
      ...filtersArticle,
      [name]: value
    };

    setFiltersArticle(nextFilters);
    setErrorsFiltersArticle(validateFormFilterArticle(nextFilters));
  }, [filtersArticle, validateFormFilterArticle]);

  const handleToggleBooleanFilter = useCallback((fieldName) => {
    setFiltersArticle((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  }, []);

  const handleCloseDataEditArticle = useCallback(() => {
    setOpenEditRowArticle(false);
    setRowEditArticle(openEditRowArticle ? rowEditArticle : emptyArticle);
    dispatch(setLoading(false));
    setIsLoadingErrorArticle(false);
  }, [dispatch, openEditRowArticle, rowEditArticle]);

  const toggleDrawerFilterEditArticle = useCallback((open) => (event) => {
    if (event?.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    if (!open) {
      handleCloseDataEditArticle();
    }
  }, [handleCloseDataEditArticle]);

  const handleExportDetails = useCallback(async () => {
    if (!dataArticle || dataArticle.length === 0) {
      return;
    }

    dispatch(setLoading(true));

    try {
      const getData = await helpHttp().get3(
        ENDPOINT,
        `articles/getarticledetailstoexcel`,
        token,
        currencyOffsetArticle,
        LIMIT_PAGE,
        filtersArticlesDetails
      );

      if (getData.err) {
        dispatch(warningErrorAction(t('Text_InvalidCredentials')));
      } else if (getData?.length > 0) {
        exportArticleExcelOneSheet(
          getData,
          "INVENTARIO_DETALLADO_ARTÍCILOS",
          permissions.showExtendedFilter
        );
      }
    } catch (error) {
      dispatch(warningErrorAction(t('Text_IncorrectData')));
    } finally {
      dispatch(setLoading(false));
    }
  }, [
    dataArticle,
    dispatch,
    token,
    currencyOffsetArticle,
    filtersArticlesDetails,
    t,
    permissions.showExtendedFilter
  ]);

  const handleChangeDeleteRowArticle = useCallback((e) => {
    setIdCodeDelete(e.target.value);
  }, []);

  const handleCancelDeleteRowArticle = useCallback(() => {
    setRowEditArticle(emptyArticle);
    dispatch(setLoading(false));
    setIsLoadingErrorArticle(false);
    setIdCodeDelete(DELETE_CODE);
    setOpenDeleteRowArticle(false);
  }, [DELETE_CODE, dispatch]);

  const handleCloseDeleteRowArticle = useCallback(async () => {
    if (idCodeDelete !== DELETE_CODE_STORE) {
      dispatch(warningErrorAction(t('Text_InvalidCode')));
      return;
    }

    if (!rowEditArticle?.id && rowEditArticle?.id !== 0) {
      dispatch(warningErrorAction(t('Text_InvalidId')));
      return;
    }

    dispatch(setLoading(true));

    try {
      const getData = await helpHttp().del(
        ENDPOINT,
        `articles/deletearticle`,
        token,
        rowEditArticle
      );

      if (getData.status === "OK") {
        dispatch(successErrorAction(t('Text_ValidCode')));
        dispatch(successErrorAction(t('Text_DeleteField')));
        setRefeshData(true);
      } else {
        dispatch(warningErrorAction(t(getData.cause)));
      }
    } catch (error) {
      dispatch(warningErrorAction(t('Text_IncorrectData')));
    } finally {
      dispatch(setLoading(false));
      setIsLoadingErrorArticle(false);
      setIdCodeDelete(DELETE_CODE);
      setOpenDeleteRowArticle(false);
      handleCloseDataEditArticle();
    }
  }, [
    DELETE_CODE,
    DELETE_CODE_STORE,
    dispatch,
    handleCloseDataEditArticle,
    idCodeDelete,
    rowEditArticle,
    t,
    token
  ]);

  const filteredDataArticle = useMemo(() => {
    const searchValue = inputTextArticle.trim();

    if (!searchValue) {
      return dataArticle;
    }

    return dataArticle.filter((el) => {
      const searchableText = [
        el.id,
        el.titlees,
        el.titleen,
        el.owncode,
        el.codean,
        el.barcode,
        el.accountingcode
      ].join(' ');

      return articleMatchesSearch(searchableText, searchValue);
    });
  }, [dataArticle, inputTextArticle]);

  const inputHandlerArticle = useCallback((e) => {
    setInputTextArticle(e.target.value);
  }, []);

  const othersFiltersActive = useMemo(() => {
    return OTHER_FILTER_KEYS.some((key) => filtersArticlesDetails[key] !== "");
  }, [filtersArticlesDetails]);

  const filterChipStates = useMemo(() => ({
    code: filtersArticlesDetails.idFrom !== "" || filtersArticlesDetails.idTo !== "",
    description: isSpanish
      ? filtersArticlesDetails.titleesFrom !== "" || filtersArticlesDetails.titleesTo !== ""
      : filtersArticlesDetails.titleenFrom !== "" || filtersArticlesDetails.titleenTo !== "",
    owncode: filtersArticlesDetails.owncodeFrom !== "" || filtersArticlesDetails.owncodeTo !== "",
    codean: filtersArticlesDetails.codeanFrom !== "" || filtersArticlesDetails.codeanTo !== "",
    barcode: filtersArticlesDetails.barcodeFrom !== "" || filtersArticlesDetails.barcodeTo !== "",
    accountingcode: filtersArticlesDetails.accountingcodeFrom !== "" || filtersArticlesDetails.accountingcodeTo !== "",
    applytypesofsize: filtersArticlesDetails.applytypesofsize,
    notapplytypesofsize: filtersArticlesDetails.notapplytypesofsize,
    applycolor: filtersArticlesDetails.applycolor,
    notapplycolor: filtersArticlesDetails.notapplycolor,
    applynumserial: filtersArticlesDetails.applynumserial,
    notapplynumserial: filtersArticlesDetails.notapplynumserial
  }), [filtersArticlesDetails, isSpanish]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event?.key === 'Escape' && showModalArticlesSettings) {
        resetArticleState();
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [resetArticleState, showModalArticlesSettings]);

  useEffect(() => {
    if (numTotRegArticle > LIMIT_PAGE) {
      setActiveOffsetArticle(true);
      setRangeOffsetArticle(buildOffsetRange(numTotRegArticle));
    } else {
      setCurrencyOffsetArticle(0);
      setActiveOffsetArticle(false);
      setRangeOffsetArticle([{}]);
    }
  }, [numTotRegArticle]);

  useEffect(() => {
    let isMounted = true;

    const fetchCatalogs = async () => {
      if (!showModalArticlesSettings) {
        return;
      }

      dispatch(setLoading(false));

      const requests = [
        {
          promise: helpHttp().get(
            ENDPOINT,
            isSpanish ? `typesofunits/getsimplesalesunitsbynamees` : `typesofunits/getsimplesalesunitsbynameen`,
            token
          ),
          setter: setTypesOfUnitsToSelect
        },
        {
          promise: helpHttp().get(
            ENDPOINT,
            isSpanish ? `department/simpledepartmentsbynamees` : `department/simpledepartmentonlybynameen`,
            token
          ),
          setter: setDepartmentToSelect
        },
        {
          promise: helpHttp().get(
            ENDPOINT,
            isSpanish ? `section/simplesectiononlybynamees` : `section/simplesectiononlybynameen`,
            token
          ),
          setter: setSectionToSelect
        },
        {
          promise: helpHttp().get(
            ENDPOINT,
            isSpanish ? `subsection/simplesubsectiononlybynamees` : `subsection/simplesubsectiononlybynameen`,
            token
          ),
          setter: setSubSectionToSelect
        }
      ];

      try {
        const responses = await Promise.all(requests.map((item) => item.promise));

        responses.forEach((response, index) => {
          if (response.err) {
            dispatch(warningErrorAction(t('Text_InvalidCredentials')));
            return;
          }

          if (isMounted) {
            requests[index].setter(response);
          }
        });
      } catch (error) {
        dispatch(warningErrorAction(t('Text_IncorrectData')));
      }
    };

    fetchCatalogs();

    return () => {
      isMounted = false;
    };
  }, [dispatch, isSpanish, showModalArticlesSettings, t, token]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!showModalArticlesSettings && !refeshData) {
        return;
      }

      dispatch(setLoading(false));

      try {
        const [totalData, countFilteredData, articlesData] = await Promise.all([
          helpHttp().get(ENDPOINT, `articles/getinformationtablearticle`, token),
          helpHttp().post(ENDPOINT, `articles/getcountfilterarticle`, token, filtersArticlesDetails),
          helpHttp().get3(
            ENDPOINT,
            `articles/getarticles`,
            token,
            currencyOffsetArticle,
            LIMIT_PAGE,
            filtersArticlesDetails
          )
        ]);

        const responses = [totalData, countFilteredData, articlesData];
        const errorResponse = responses.find((response) => response?.err || response?.message === "Load failed");

        if (errorResponse) {
          if (isMounted) {
            setDataArticle(initialStateArticle);
            dispatch(
              warningErrorAction(
                t(errorResponse.message === "Load failed" ? 'Text_ConnectionFailied' : 'Text_InvalidCredentials')
              )
            );
          }
          return;
        }

        if (isMounted) {
          setNumTotRegArticle(totalData);
          setNumTotRegFilter(countFilteredData);
          setDataArticle(articlesData);
        }
      } catch (error) {
        dispatch(warningErrorAction(t('Text_IncorrectData')));
      } finally {
        if (isMounted) {
          setRefeshData(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [
    currencyOffsetArticle,
    dispatch,
    filtersArticlesDetails,
    refeshData,
    showModalArticlesSettings,
    t,
    token
  ]);

  const titleFromName = isSpanish ? "titleesFrom" : "titleenFrom";
  const titleToName = isSpanish ? "titleesTo" : "titleenTo";
  const titleFromValue = isSpanish ? filtersArticle.titleesFrom : filtersArticle.titleenFrom;
  const titleToValue = isSpanish ? filtersArticle.titleesTo : filtersArticle.titleenTo;

  const salesUnitsFromName = isSpanish ? "salesunitsFromES" : "salesunitsFromEN";
  const salesUnitsToName = isSpanish ? "salesunitsToES" : "salesunitsToEN";
  const salesUnitsFromValue = isSpanish ? filtersArticle.salesunitsFromES : filtersArticle.salesunitsFromEN;
  const salesUnitsToValue = isSpanish ? filtersArticle.salesunitsToES : filtersArticle.salesunitsToEN;

  const departmentFromName = isSpanish ? "departmentFromES" : "departmentFromEN";
  const departmentToName = isSpanish ? "departmentToES" : "departmentToEN";
  const departmentFromValue = isSpanish ? filtersArticle.departmentFromES : filtersArticle.departmentFromEN;
  const departmentToValue = isSpanish ? filtersArticle.departmentToES : filtersArticle.departmentToEN;

  const sectionFromName = isSpanish ? "sectionFromES" : "sectionFromEN";
  const sectionToName = isSpanish ? "sectionToES" : "sectionToEN";
  const sectionFromValue = isSpanish ? filtersArticle.sectionFromES : filtersArticle.sectionFromEN;
  const sectionToValue = isSpanish ? filtersArticle.sectionToES : filtersArticle.sectionToEN;

  const subsectionFromName = isSpanish ? "subsectionFromES" : "subsectionFromEN";
  const subsectionToName = isSpanish ? "subsectionToES" : "subsectionToEN";
  const subsectionFromValue = isSpanish ? filtersArticle.subsectionFromES : filtersArticle.subsectionFromEN;
  const subsectionToValue = isSpanish ? filtersArticle.subsectionToES : filtersArticle.subsectionToEN;

  return (
    <>
      {showModalArticlesSettings ? (
        <div className="container-fluid formdi-pending-page">
          <div id="myDIV" className="formdi-pending-header">
            <h1 className="formdi-pending-title">
              {isLoadingErrorArticle
                ? <i className="fas fa-solid fa-exclamation fa-2x text-danger"></i>
                : isLoadingData
                  ? <CircularProgress color={openDeleteRowArticle ? "warning" : "primary"} />
                  : <i className="fas fa-barcode-card text-warning"></i>
              } {t('Text_Articles_Table')}
            </h1>
            <div className="formdi-pending-header-actions"> 
              {permissions.showCreateButton ? (
                <div>
                  <button
                    type="button"
                    className="formdi-pending-btn formdi-pending-btn--primary"
                    onClick={openCreateOrEditDataArticle}
                  >
                    {t('Button_Create')}
                  </button>
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  <button
                    type="button"
                    className="formdi-pending-btn formdi-pending-btn--secondary"
                    onClick={closeModalArticles}
                  >
                    {t('Button_Back')}
                  </button>
                </div>
              ) : (
                <div>
                  <button
                    type="button"
                    className="formdi-pending-btn formdi-pending-btn--secondary"
                    onClick={closeModalArticles}
                  >
                    {t('Button_Back')}
                  </button>
                </div>
              )}
              <div
                className="video-tutorial-cta"
                onClick={() => setOpenVideoHelpStore(true)}
                title={t('Text_Video_View')}
              >
                <div className="video-tutorial-cta__circle">
                  <i className="fas fa-play"></i>
                </div>

                <div className="video-tutorial-cta__pill">
                  <span className="video-tutorial-cta__tag">{t('Text_Video_Tutorial')}</span>
                  <span className="video-tutorial-cta__title">{t('Button_Video')}</span>
                </div>
              </div> 
            </div> 
          </div>
          <br />
          <div className="formdi-pending-search">
            <TextField
              id="article-list-search"
              autoComplete="off"
              variant="outlined"
              inputProps={{ style: { fontSize: '18px' } }}
              InputLabelProps={{ style: { fontSize: '18px', background: '#f8f9fc' } }}
              value={inputTextArticle}
              placeholder={t('Text_Article_Search')}
              onChange={inputHandlerArticle}
              fullWidth
              label={t('Text_Article_Search')}
            />
          </div>

          <br />

          <div id="myNewDIV" className="formdi-pending-filters">
            <h1 className="formdi-pending-filter-title">{t("Text_Filter")}</h1>

            <div className="formdi-pending-filters-row">
              <FilterChip
                active={filterChipStates.code}
                label={t("Text_Code")}
                onClick={openEditFiltersArticle}
              />

              <FilterChip
                active={filterChipStates.description}
                label={t("Text_Description")}
                onClick={openEditFiltersArticle}
              />

              <FilterChip
                active={filterChipStates.owncode}
                label={t("Text_Owncode")}
                onClick={openEditFiltersArticle}
              />

              <FilterChip
                active={filterChipStates.codean}
                label={t("Text_Codean")}
                onClick={openEditFiltersArticle}
              />

              <FilterChip
                active={filterChipStates.barcode}
                label={t("Text_Barcode")}
                onClick={openEditFiltersArticle}
              />

               <FilterChip
                active={filterChipStates.accountingcode}
                label={t("Text_Accountingcode")}
                onClick={openEditFiltersArticle}
              />

              {permissions.showExtendedFilter && (
                <>
                  <FilterChip
                    active={filterChipStates.applytypesofsize}
                    label={t("Text_Filter_Applytypesofsize")}
                    onClick={openEditFiltersArticle}
                  />
                  <FilterChip
                    active={filterChipStates.notapplytypesofsize}
                    label={t("Text_Filter_Notapplytypesofsize")}
                    onClick={openEditFiltersArticle}
                  />
                  <FilterChip
                    active={filterChipStates.applycolor}
                    label={t("Text_Filter_Applycolor")}
                    onClick={openEditFiltersArticle}
                  />
                  <FilterChip
                    active={filterChipStates.notapplycolor}
                    label={t("Text_Filter_Notapplycolor")}
                    onClick={openEditFiltersArticle}
                  />
                  <FilterChip
                    active={filterChipStates.applynumserial}
                    label={t("Text_Filter_Applynumserial")}
                    onClick={openEditFiltersArticle}
                  />
                  <FilterChip
                    active={filterChipStates.notapplynumserial}
                    label={t("Text_Filter_Applynotnumserial")}
                    onClick={openEditFiltersArticle}
                  />
                </>
              )}

              <FilterChip
                active={othersFiltersActive}
                label={t("Text_Others_Filters")}
                onClick={openEditFiltersArticle}
              />

              {permissions.showExcelButton && (
                <>
                  <button
                    type="button"
                    className="formdi-excel-chip"
                    onClick={handleExportDetails}
                  >
                    {t('Button_Excel')}
                  </button>
                  <> </>
                </>
              )}
            </div>
          </div>

          <br />

          {!isLoadingData ? (
            <>
              <div className="formdi-pending-card-flat">
                <div className="formdi-pending-card-content">
                  <TableSortingArticle
                    dataArticle={filteredDataArticle}
                    numTotRegFilter={numTotRegFilter}
                    titleTable={t('Text_Articles_Table')}
                    setOpenDeleteRowArticle={setOpenDeleteRowArticle}
                    setRowEditArticle={setRowEditArticle}
                    showEditButtonArticle={permissions.showEditButton}
                    showDeleteButtonArticle={permissions.showDeleteButton}
                    numTotRegArticle={numTotRegArticle}
                    activeOffsetArticle={activeOffsetArticle}
                    rangeOffsetArticle={rangeOffsetArticle}
                    currencyOffsetArticle={currencyOffsetArticle}
                    setCurrencyOffsetArticle={setCurrencyOffsetArticle}
                    setOpenViewDetails={setOpenViewDetails}
                    minvalue={Number(NON_ERASABLE_ARTICLE_ID)}
                    showExtendedFilter={permissions.showExtendedFilter}
                  />
                </div>
              </div>

              {openEditFilterArticle && (
                <div>
                  <SwipeableDrawer
                    anchor='right'
                    open={true}
                    onClose={toggleDrawerFilterEditArticle(false)}
                    onOpen={toggleDrawerFilterEditArticle(true)}
                  >
                    <Container>
                      <Box sx={{ bgcolor: '#ffffff', height: '100vh', width: '850' }}>
                        <br />
                        <div id="myDIV">
                          <h1 className="h1NewStyle px-4 mb-2">{t('Text_Filter')}</h1>
                        </div>
                        <hr className="sidebar-divider d-none d-md-block" />

                        <form className="createfilteemployee" onSubmit={handleCancelFiltersArticle}>
                          <div className="form-floating mb-3 px-4 primary">
                            <RangeTextField
                              fromId="idFrom"
                              toId="idTo"
                              fromName="idFrom"
                              toName="idTo"
                              fromValue={filtersArticle.idFrom}
                              toValue={filtersArticle.idTo}
                              fromLabel={errorsFiltersArticle.idFrom != null ? t(errorsFiltersArticle.idFrom) : t('Text_Filter_Code_From')}
                              toLabel={errorsFiltersArticle.idTo != null ? t(errorsFiltersArticle.idTo) : t('Text_Filter_Code_To')}
                              fromPlaceholder={t('PlaceHolder_Filter_Code_From')}
                              toPlaceholder={t('PlaceHolder_Filter_Code_to')}
                              onBlur={handleBlurFilterEditArticle}
                              onChange={handleChangeFilterEditArticle}
                            />

                            <RangeTextField
                              fromId={titleFromName}
                              toId={titleToName}
                              fromName={titleFromName}
                              toName={titleToName}
                              fromValue={titleFromValue}
                              toValue={titleToValue}
                              fromLabel={
                                isSpanish
                                  ? (errorsFiltersArticle.titleesFrom != null ? t(errorsFiltersArticle.titleesFrom) : t('Text_Filter_titleFrom'))
                                  : (errorsFiltersArticle.titleenFrom != null ? t(errorsFiltersArticle.titleenFrom) : t('PlaceHolder_Filter_titleFrom'))
                              }
                              toLabel={
                                isSpanish
                                  ? (errorsFiltersArticle.titleesTo != null ? t(errorsFiltersArticle.titleesTo) : t('Text_Filter_titleTo'))
                                  : (errorsFiltersArticle.titleenTo != null ? t(errorsFiltersArticle.titleenTo) : t('PlaceHolder_Filter_titleTo'))
                              }
                              fromPlaceholder={t('PlaceHolder_Filter_titleFrom')}
                              toPlaceholder={t('PlaceHolder_Filter_titleTo')}
                              onBlur={handleBlurFilterEditArticle}
                              onChange={handleChangeFilterEditArticle}
                            />

                            <RangeTextField
                              fromId="owncodeFrom"
                              toId="owncodeTo"
                              fromName="owncodeFrom"
                              toName="owncodeTo"
                              fromValue={filtersArticle.owncodeFrom}
                              toValue={filtersArticle.owncodeTo}
                              fromLabel={errorsFiltersArticle.owncodeFrom != null ? t(errorsFiltersArticle.owncodeFrom) : t('Text_Filter_owncodeFrom')}
                              toLabel={errorsFiltersArticle.owncodeTo != null ? t(errorsFiltersArticle.owncodeTo) : t('Text_Filter_owncodeTo')}
                              fromPlaceholder={t('PlaceHolder_Filter_owncodeFrom')}
                              toPlaceholder={t('PlaceHolder_Filter_owncodeTo')}
                              onBlur={handleBlurFilterEditArticle}
                              onChange={handleChangeFilterEditArticle}
                            />

                            <RangeTextField
                              fromId="codeanFrom"
                              toId="codeanTo"
                              fromName="codeanFrom"
                              toName="codeanTo"
                              fromValue={filtersArticle.codeanFrom}
                              toValue={filtersArticle.codeanTo}
                              fromLabel={errorsFiltersArticle.codeanFrom != null ? t(errorsFiltersArticle.codeanFrom) : t('Text_Filter_codeanFrom')}
                              toLabel={errorsFiltersArticle.codeanTo != null ? t(errorsFiltersArticle.codeanTo) : t('Text_Filter_codeanTo')}
                              fromPlaceholder={t('PlaceHolder_Filter_codeanFrom')}
                              toPlaceholder={t('PlaceHolder_Filter_codeanTo')}
                              onBlur={handleBlurFilterEditArticle}
                              onChange={handleChangeFilterEditArticle}
                            />

                            <RangeTextField
                              fromId="barcodeFrom"
                              toId="barcodeTo"
                              fromName="barcodeFrom"
                              toName="barcodeTo"
                              fromValue={filtersArticle.barcodeFrom}
                              toValue={filtersArticle.barcodeTo}
                              fromLabel={errorsFiltersArticle.barcodeFrom != null ? t(errorsFiltersArticle.barcodeFrom) : t('Text_Filter_barcodeFrom')}
                              toLabel={errorsFiltersArticle.barcodeTo != null ? t(errorsFiltersArticle.barcodeTo) : t('Text_Filter_barcodeTo')}
                              fromPlaceholder={t('PlaceHolder_Filter_barcodeFrom')}
                              toPlaceholder={t('PlaceHolder_Filter_barcodeTo')}
                              onBlur={handleBlurFilterEditArticle}
                              onChange={handleChangeFilterEditArticle}
                            />
                             <RangeTextField
                              fromId="accountingcodeFrom"
                              toId="accountingcodeTo"
                              fromName="accountingcodeFrom"
                              toName="accountingcodeTo"
                              fromValue={filtersArticle.accountingcodeFrom}
                              toValue={filtersArticle.accountingcodeTo}
                              fromLabel={errorsFiltersArticle.accountingcodeFrom != null ? t(errorsFiltersArticle.accountingcodeFrom) : t('Text_Filter_accountingcodeFrom')}
                              toLabel={errorsFiltersArticle.accountingcodeTo != null ? t(errorsFiltersArticle.accountingcodeTo) : t('Text_Filter_accountingcodeTo')}
                              fromPlaceholder={t('PlaceHolder_Filter_accountingcodeFrom')}
                              toPlaceholder={t('PlaceHolder_Filter_accountingcodeTo')}
                              onBlur={handleBlurFilterEditArticle}
                              onChange={handleChangeFilterEditArticle}
                            />

                            {permissions.showExtendedFilter && (
                              <RangeTextField
                                fromId="locationFrom"
                                toId="locationTo"
                                fromName="locationFrom"
                                toName="locationTo"
                                fromValue={filtersArticle.locationFrom}
                                toValue={filtersArticle.locationTo}
                                fromLabel={errorsFiltersArticle.locationFrom != null ? t(errorsFiltersArticle.locationFrom) : t('Text_Filter_locationFrom')}
                                toLabel={errorsFiltersArticle.locationTo != null ? t(errorsFiltersArticle.locationTo) : t('Text_Filter_locationTo')}
                                fromPlaceholder={t('PlaceHolder_Filter_locationFrom')}
                                toPlaceholder={t('PlaceHolder_Filter_locationTo')}
                                onBlur={handleBlurFilterEditArticle}
                                onChange={handleChangeFilterEditArticle}
                              />
                            )}

                            <RangeSelectField
                              fromId={salesUnitsFromName}
                              toId={salesUnitsToName}
                              fromName={salesUnitsFromName}
                              toName={salesUnitsToName}
                              fromValue={salesUnitsFromValue}
                              toValue={salesUnitsToValue}
                              fromLabel={
                                isSpanish
                                  ? (errorsFiltersArticle.salesunitsFromES != null ? t(errorsFiltersArticle.salesunitsFromES) : t('Text_Filter_salesunitsFrom'))
                                  : (errorsFiltersArticle.salesunitsFromEN != null ? t(errorsFiltersArticle.salesunitsFromEN) : t('Text_Filter_salesunitsFrom'))
                              }
                              toLabel={
                                isSpanish
                                  ? (errorsFiltersArticle.salesunitsToES != null ? t(errorsFiltersArticle.salesunitsToES) : t('Text_Filter_salesunitsTo'))
                                  : (errorsFiltersArticle.salesunitsToEN != null ? t(errorsFiltersArticle.salesunitsToEN) : t('Text_Filter_salesunitsTo'))
                              }
                              fromPlaceholder={t('PlaceHolder_Filter_salesunitsFrom')}
                              toPlaceholder={t('PlaceHolder_Filter_salesunitsTo')}
                              options={typesOfUnitsToSelect}
                              optionTextKey={selectLabelKey}
                              onBlur={handleBlurFilterEditArticle}
                              onChange={handleChangeFilterEditArticle}
                            />

                            <RangeSelectField
                              fromId={departmentFromName}
                              toId={departmentToName}
                              fromName={departmentFromName}
                              toName={departmentToName}
                              fromValue={departmentFromValue}
                              toValue={departmentToValue}
                              fromLabel={
                                isSpanish
                                  ? (errorsFiltersArticle.departmentFromES != null ? t(errorsFiltersArticle.departmentFromES) : t('Text_Filter_departmentFrom'))
                                  : (errorsFiltersArticle.departmentFromEN != null ? t(errorsFiltersArticle.departmentFromEN) : t('Text_Filter_departmentFrom'))
                              }
                              toLabel={
                                isSpanish
                                  ? (errorsFiltersArticle.departmentToES != null ? t(errorsFiltersArticle.departmentToES) : t('Text_Filter_departmentTo'))
                                  : (errorsFiltersArticle.departmentToEN != null ? t(errorsFiltersArticle.departmentToEN) : t('Text_Filter_departmentTo'))
                              }
                              fromPlaceholder={t('PlaceHolder_Filter_departmentFrom')}
                              toPlaceholder={t('PlaceHolder_Filter_departmentTo')}
                              options={departmentToSelect}
                              optionTextKey={selectLabelKey}
                              onBlur={handleBlurFilterEditArticle}
                              onChange={handleChangeFilterEditArticle}
                            />

                            <RangeSelectField
                              fromId={sectionFromName}
                              toId={sectionToName}
                              fromName={sectionFromName}
                              toName={sectionToName}
                              fromValue={sectionFromValue}
                              toValue={sectionToValue}
                              fromLabel={
                                isSpanish
                                  ? (errorsFiltersArticle.sectionFromES != null ? t(errorsFiltersArticle.sectionFromES) : t('Text_Filter_sectionFrom'))
                                  : (errorsFiltersArticle.sectionFromEN != null ? t(errorsFiltersArticle.sectionFromEN) : t('Text_Filter_sectionFrom'))
                              }
                              toLabel={
                                isSpanish
                                  ? (errorsFiltersArticle.sectionToES != null ? t(errorsFiltersArticle.sectionToES) : t('Text_Filter_sectionTo'))
                                  : (errorsFiltersArticle.sectionToEN != null ? t(errorsFiltersArticle.sectionToEN) : t('Text_Filter_sectionTo'))
                              }
                              fromPlaceholder={t('PlaceHolder_Filter_sectionFrom')}
                              toPlaceholder={t('PlaceHolder_Filter_sectionTo')}
                              options={sectionToSelect}
                              optionTextKey={selectLabelKey}
                              onBlur={handleBlurFilterEditArticle}
                              onChange={handleChangeFilterEditArticle}
                            />

                            <RangeSelectField
                              fromId={subsectionFromName}
                              toId={subsectionToName}
                              fromName={subsectionFromName}
                              toName={subsectionToName}
                              fromValue={subsectionFromValue}
                              toValue={subsectionToValue}
                              fromLabel={
                                isSpanish
                                  ? (errorsFiltersArticle.subsectionFromES != null ? t(errorsFiltersArticle.subsectionFromES) : t('Text_Filter_subsectionFrom'))
                                  : (errorsFiltersArticle.subsectionFromEN != null ? t(errorsFiltersArticle.subsectionFromEN) : t('Text_Filter_subsectionFrom'))
                              }
                              toLabel={
                                isSpanish
                                  ? (errorsFiltersArticle.subsectionToES != null ? t(errorsFiltersArticle.subsectionToES) : t('Text_Filter_subsectionTo'))
                                  : (errorsFiltersArticle.subsectionToEN != null ? t(errorsFiltersArticle.subsectionToEN) : t('Text_Filter_subsectionTo'))
                              }
                              fromPlaceholder={t('PlaceHolder_Filter_subsectionFrom')}
                              toPlaceholder={t('PlaceHolder_Filter_subsectionTo')}
                              options={subSectionToSelect}
                              optionTextKey={selectLabelKey}
                              onBlur={handleBlurFilterEditArticle}
                              onChange={handleChangeFilterEditArticle}
                            />

                            {permissions.showExtendedFilter && (
                              <>
                                <RangeTextField
                                  fromId="minimumstockFrom"
                                  toId="minimumstockTo"
                                  fromName="minimumstockFrom"
                                  toName="minimumstockTo"
                                  fromValue={filtersArticle.minimumstockFrom}
                                  toValue={filtersArticle.minimumstockTo}
                                  fromLabel={errorsFiltersArticle.minimumstockFrom != null ? t(errorsFiltersArticle.minimumstockFrom) : t('Text_Filter_minimumstockFrom')}
                                  toLabel={errorsFiltersArticle.minimumstockTo != null ? t(errorsFiltersArticle.minimumstockTo) : t('Text_Filter_minimumstockTo')}
                                  fromPlaceholder={t('PlaceHolder_Filter_minimumstockFrom')}
                                  toPlaceholder={t('PlaceHolder_Filter_minimumstockTo')}
                                  onBlur={handleBlurFilterEditArticle}
                                  onChange={handleChangeFilterEditArticle}
                                />

                                <RangeTextField
                                  fromId="maximumstockFrom"
                                  toId="maximumstockTo"
                                  fromName="maximumstockFrom"
                                  toName="maximumstockTo"
                                  fromValue={filtersArticle.maximumstockFrom}
                                  toValue={filtersArticle.maximumstockTo}
                                  fromLabel={errorsFiltersArticle.maximumstockFrom != null ? t(errorsFiltersArticle.maximumstockFrom) : t('Text_Filter_maximumstockFrom')}
                                  toLabel={errorsFiltersArticle.maximumstockTo != null ? t(errorsFiltersArticle.maximumstockTo) : t('Text_Filter_maximumstockTo')}
                                  fromPlaceholder={t('PlaceHolder_Filter_maximumstockFrom')}
                                  toPlaceholder={t('PlaceHolder_Filter_maximumstockTo')}
                                  onBlur={handleBlurFilterEditArticle}
                                  onChange={handleChangeFilterEditArticle}
                                />

                                <RangeTextField
                                  fromId="actualstockFrom"
                                  toId="actualstockTo"
                                  fromName="actualstockFrom"
                                  toName="actualstockTo"
                                  fromValue={filtersArticle.actualstockFrom}
                                  toValue={filtersArticle.actualstockTo}
                                  fromLabel={errorsFiltersArticle.actualstockFrom != null ? t(errorsFiltersArticle.actualstockFrom) : t('Text_Filter_actualstockFrom')}
                                  toLabel={errorsFiltersArticle.actualstockTo != null ? t(errorsFiltersArticle.actualstockTo) : t('Text_Filter_actualstockTo')}
                                  fromPlaceholder={t('PlaceHolder_Filter_actualstockFrom')}
                                  toPlaceholder={t('PlaceHolder_Filter_actualstockTo')}
                                  onBlur={handleBlurFilterEditArticle}
                                  onChange={handleChangeFilterEditArticle}
                                />

                                <BooleanFilterPair
                                  leftId="applytypesofsize"
                                  leftName="applytypesofsize"
                                  leftChecked={filtersArticle.applytypesofsize}
                                  leftLabel={t('Text_Filter_applytypesofsize')}
                                  rightId="notapplytypesofsize"
                                  rightName="notapplytypesofsize"
                                  rightChecked={filtersArticle.notapplytypesofsize}
                                  rightLabel={t('Text_Filter_notapplytypesofsize')}
                                  onToggle={handleToggleBooleanFilter}
                                />

                                <BooleanFilterPair
                                  leftId="applycolor"
                                  leftName="applycolor"
                                  leftChecked={filtersArticle.applycolor}
                                  leftLabel={t('Text_Filter_applycolor')}
                                  rightId="notapplycolor"
                                  rightName="notapplycolor"
                                  rightChecked={filtersArticle.notapplycolor}
                                  rightLabel={t('Text_Filter_notapplycolor')}
                                  onToggle={handleToggleBooleanFilter}
                                />

                                <BooleanFilterPair
                                  leftId="applynumserial"
                                  leftName="applynumserial"
                                  leftChecked={filtersArticle.applynumserial}
                                  leftLabel={t('Text_Filter_applynumserial')}
                                  rightId="notapplynumserial"
                                  rightName="notapplynumserial"
                                  rightChecked={filtersArticle.notapplynumserial}
                                  rightLabel={t('Text_Filter_applynumserial')}
                                  onToggle={handleToggleBooleanFilter}
                                />

                                <BooleanFilterPair
                                  leftId="isnewpiece"
                                  leftName="isnewpiece"
                                  leftChecked={filtersArticle.isnewpiece}
                                  leftLabel={t('Text_Filter_isnewpiece')}
                                  rightId="notisnewpiece"
                                  rightName="notisnewpiece"
                                  rightChecked={filtersArticle.notisnewpiece}
                                  rightLabel={t('Text_Filter_notisnewpiece')}
                                  onToggle={handleToggleBooleanFilter}
                                />
                              </>
                            )}
                          </div>

                          <hr className="sidebar-divider d-none d-md-block" />

                          <div className="formdi-pending-action-group">
                            <button
                              className="formdi-pending-action-btn formdi-pending-action-btn--primary"
                              type="button"
                              onClick={handleSubmitFilterEditArticle}
                            >
                              {t('Button_Apply')}
                            </button>

                            <button
                              className="formdi-pending-action-btn formdi-pending-action-btn--secondary"
                              type="button"
                              onClick={handleDeleteFiltersStore}
                            >
                              {t('Button_Cancel_Filters')}
                            </button>
                          </div>
                        </form>
                      </Box>
                    </Container>
                  </SwipeableDrawer>
                </div>
              )}

              <ViewArticleDetails
                id={rowEditArticle.id}
                openViewDetails={openViewDetails}
                setOpenViewDetails={setOpenViewDetails}
              />
              <Dialog
                open={openVideoHelpStore}
                onClose={() => setOpenVideoHelpStore(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                  style: {
                    borderRadius: "20px",
                    overflow: "hidden"
                  }
                }}
              >
                <DialogTitle
                  sx={{
                    color: "#4e73df",
                    fontWeight: 500,
                    fontSize: "1.4rem"
                  }}
                >
                  {t('Text_Video_Explain_Articles').toUpperCase()}
                </DialogTitle> 
                <DialogContent sx={{ padding: 0, background: "#000" }}>
                  <div className="store-video-wrapper">
                    <iframe
                      width="100%"
                      height="100%"
                      src={STORE_VIDEO_URL}
                      title={t('Text_Video_Explain')}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </DialogContent>

                <DialogActions sx={{ padding: "12px 20px" }}>
                  <Button
                    onClick={() => setOpenVideoHelpStore(false)}
                    className="my-dialog-button-cancel"
                  >
                    {t('Button_Close')}
                  </Button>
                </DialogActions>
              </Dialog>
              <Dialog
                open={openDeleteRowArticle}
                onClose={handleCancelDeleteRowArticle}
                PaperProps={{ className: "my-dialog-paper-danger" }}
              >
                <DialogTitle className="my-dialog-title-danger">
                  {t('Text_Delete')} {rowEditArticle.id} - {rowEditArticle.titlees}
                </DialogTitle>

                <DialogContent>
                  <DialogContentText className="my-dialog-contentText-danger">
                    {t("Text_Line1")}<br />
                    {t("Text_Line2")}
                  </DialogContentText>

                  <TextField
                    autoFocus
                    margin="dense"
                    id="idCode"
                    label={t("Text_Label")}
                    type="text"
                    fullWidth
                    variant="standard"
                    className="my-dialog-input-danger"
                    value={idCodeDelete}
                    onChange={handleChangeDeleteRowArticle}
                  />
                </DialogContent>

                <DialogActions className="my-dialog-actions">
                  <Button
                    onClick={handleCancelDeleteRowArticle}
                    className="my-dialog-button-cancel-danger"
                  >
                    {t("Button_Cancel")}
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleCloseDeleteRowArticle}
                    className="my-dialog-button-delete"
                  >
                    {t("Button_Delete")}
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          ) : (
            <LoadingNews />
          )}
        </div>
      ) : null}
    </>
  );
};

ArticleFilter.propTypes = {
  showModalArticlesSettings: PropTypes.bool.isRequired,
  setShowModalArticlesSettings: PropTypes.func.isRequired
};

export default ArticleFilter;